import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemDecorator,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { ApiCircuit, ApiCompetition } from "griptonite-api-client";
import { useCallback } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useVenueCompetitionsQuery } from "@/hooks/competitions";

import { useVenueCircuitsQuery } from "../../hooks/routes";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { useModal } from "../modals/useModal";
import { RouteCircle } from "../RouteCircle";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

const CircuitItem = ({ circuit }: { circuit?: ApiCircuit }) => {
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: 1 }}>
        <RouteCircle colors={circuit?.colors.join(":").split(":") || ["#FFFFFF"]} />
      </Box>
      <Typography fontWeight="bold" fontSize={14}>
        {circuit?.name}
      </Typography>
    </ListItemDecorator>
  );
};

const CompetitionItem = ({ competition }: { competition?: ApiCompetition }) => {
  const color = competition ? "#0075FF" : "##FFFFFF";
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: 1 }}>
        <RouteCircle colors={[color]} />
      </Box>
      <Typography fontWeight="bold" fontSize={14}>
        <Typography textColor="primary.blue2">Event: </Typography>
        {competition?.name}
      </Typography>
    </ListItemDecorator>
  );
};

export const CompetitionSelect = ({
  name = "competition",
  disabled,
}: {
  name?: string;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const currentVenueUuid = useCurrentVenueUuid();
  let { data: competitions } = useVenueCompetitionsQuery(currentVenueUuid);

  competitions = competitions?.filter((c) => c.status !== "ENDED");

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Competition</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <Select
        value={field.value}
        onChange={(_, value) => {
          field.onChange(value);
        }}
        onClose={field.onBlur}
        disabled={disabled}
        renderValue={(option) => {
          return (
            <CompetitionItem competition={competitions?.find((c) => c.uuid === option?.value)} />
          );
        }}
      >
        {competitions?.map((competition) => (
          <Option key={competition.uuid} value={competition.uuid} label={competition.name}>
            <CompetitionItem competition={competition} />
          </Option>
        ))}
      </Select>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
};

export default function CircuitSelect({
  name = "circuit.uuid",
  withCompetitions = false,
  competitionFieldName = "competition",
  disabled,
  canDetachFromCompetition = true,
  disabledDueToActiveCompetition = false,
}: {
  name?: string;
  disabled?: boolean;
  withCompetitions?: boolean;
  competitionFieldName?: string;
  canDetachFromCompetition?: boolean;
  disabledDueToActiveCompetition?: boolean;
}) {
  // const [field, meta, fieldHelper] = useField<string | null>("circuit");
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const { field: competitionField, fieldState: competitionFieldState } = useController({
    name: competitionFieldName,
    control,
  });

  const currentVenueUuid = useCurrentVenueUuid();
  const { data: circuits } = useVenueCircuitsQuery(currentVenueUuid);

  const competitionsSelect = useCallback((competitions: ApiCompetition[]) => {
    return competitions.filter((c) => c.status !== "ENDED" && c.climbType === "BOULDER");
  }, []);

  const { data: competitions } = useVenueCompetitionsQuery(
    withCompetitions ? currentVenueUuid : undefined,
    competitionsSelect,
  );

  const { display: displayModal } = useModal();

  return (
    <FormControl
      error={
        Boolean(fieldState.error) || (withCompetitions && Boolean(competitionFieldState.error))
      }
      onClick={
        disabledDueToActiveCompetition
          ? () => {
              displayModal("GENERIC_MODAL", {
                title: "Cannot move competition route",
                body: (
                  <Typography>
                    <p>
                      This route is part of an active competition and cannot be moved until the
                      competition has ended.
                    </p>
                    <p>
                      After the comp is ended and results are published, you will have the option to
                      put all the routes into a new circuit.
                    </p>

                    <p>If you really need to move this route, please contact support.</p>
                  </Typography>
                ),
              });
            }
          : undefined
      }
    >
      <FormLabel>Circuit</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <Select
        value={
          field.value
            ? `circuit:${field.value}`
            : withCompetitions
              ? `competition:${competitionField.value}`
              : null
        }
        onChange={(_, value) => {
          if (value) {
            const [prefix, rest] = value.split(":");
            if (prefix === "competition") {
              field.onChange(null);
              if (withCompetitions) {
                competitionField.onChange(rest);
              }
            } else {
              field.onChange(rest);
              if (withCompetitions && canDetachFromCompetition) {
                competitionField.onChange(null);
              }
            }
          } else {
            field.onChange(null);
            if (withCompetitions) {
              competitionField.onChange(null);
            }
          }
        }}
        onClose={field.onBlur}
        disabled={disabled || disabledDueToActiveCompetition}
        renderValue={(option) => {
          if (!option || option.value === null) {
            return <CircuitItem />;
          }
          const [prefix, rest] = option.value.split(":");
          if (prefix === "competition") {
            return <CompetitionItem competition={competitions?.find((c) => c.uuid === rest)} />;
          }
          return <CircuitItem circuit={circuits?.find((c) => c.uuid === rest)} />;
        }}
      >
        {competitions?.map((competition) => (
          <Option
            key={competition.uuid}
            value={"competition:" + competition.uuid}
            label={competition.name}
          >
            <CompetitionItem key={competition.uuid} competition={competition} />
          </Option>
        ))}
        {circuits?.map((circuit) => (
          <Option key={circuit.uuid} value={"circuit:" + circuit.uuid} label={circuit.name}>
            <CircuitItem circuit={circuit} />
          </Option>
        ))}
      </Select>
      <FormHelperText>
        {fieldState.error?.message || competitionFieldState.error?.message}
      </FormHelperText>
    </FormControl>
  );
}
